import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { SG_FETCH_TASK_SCHEDULE } from "constants/actions";
import styled from "styled-components";
import ComparisonParticipationTab from "./ComparisonParticipationTab";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #2A3039;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #666D79;
  margin: 0;
`;

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 12px;
`;

const Crumb = styled.span`
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  color: ${props => (props.clickable ? '#2D70E2' : '#6c757d')};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const TabContainer = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #E5E8EB;
  padding: 24px;
`;

const TabNavigation = styled.div`
  display: flex;
  border-bottom: 1px solid #E5E8EB;
  margin-bottom: 30px;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: 20px 0 8px 0;
  margin-right: 40px;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? '#2D70E2' : '#666D79'};
  border-bottom: ${props => props.active ? '2px solid #2D70E2' : 'none'};
  transition: all 0.2s ease;
  margin-bottom: -1px;

  &:hover {
    color: #2D70E2;
  }
`;

const TabContent = styled.div`
  min-height: 500px;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 16px;
  border-radius: 6px;
  border: 1px solid #f5c6cb;
  margin-bottom: 24px;
`;

// Quadrant Chart Styles
const QuadrantContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuadrantContainer2 = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  gap: 24px;
`;

const QuadrantChart = styled.div`
  height: 500px;
  width: 500px;
  position: relative;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 30px;
  box-sizing: border-box;
`;

const QuadrantGrid = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: white;
`;

const QuadrantSection = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  
  &.top-left {
    top: 0;
    left: 0;
    width: 50%;
    height: 50%;
    padding: 20px;
    box-sizing: border-box;
  }
  
  &.top-right {
    top: 0;
    right: 0;
    width: 50%;
    height: 50%;
    padding: 20px;
    box-sizing: border-box;
  }
  
  &.bottom-left {
    bottom: 0;
    left: 0;
    width: 50%;
    height: 50%;
    padding: 20px;
    box-sizing: border-box;
  }
  
  &.bottom-right {
    bottom: 0;
    right: 0;
    width: 50%;
    height: 50%;
    padding: 20px;
    box-sizing: border-box;
  }
`;

const ExcellenceBox = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 120px;
  height: 60px;
  border: 2px dashed #333;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.9);
`;

const QuadrantTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-align: center;
  text-transform: uppercase;
  line-height: 1.2;
`;

const ExcellenceTitle = styled.div`
  font-size: 10px;
  color: #666;
  margin-bottom: 2px;
`;

const ExcellenceText = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #333;
  text-transform: uppercase;
`;

const DataPoint = styled.div`
  position: absolute;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: all 0.3s ease;
  cursor: pointer;
  z-index: 15;
  
  &.current {
    background-color: #007bff;
    border: 3px solid white;
    box-shadow: 0 3px 12px rgba(0, 123, 255, 0.6);
    z-index: 20; /* Make sure blue dot is on top */
  }
  
  &.updated {
    background-color: #28a745;
    border: 2px solid white;
    box-shadow: 0 2px 8px rgba(40, 167, 69, 0.4);
    z-index: 16;
  }
  
  &:hover {
    width: 22px;
    height: 22px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    z-index: 25;
  }
`;

const TraceLine = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 5;
  pointer-events: none;
`;

const QuadrantAxis = styled.div`
  position: absolute;
  width: 100%;
  height: 1px;
  background-color: #333;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
`;

const QuadrantAxisVertical = styled.div`
  position: absolute;
  height: 100%;
  width: 1px;
  background-color: #333;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
`;

const AxisTicks = styled.div`
  position: absolute;
  
  &.horizontal {
    bottom: -20px;
    left: 0;
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0 2px;
  }
  
  &.vertical {
    left: -20px;
    top: 0;
    height: 100%;
    display: flex;
    flex-direction: column-reverse;
    justify-content: space-between;
    padding: 2px 0;
  }
`;

const AxisTick = styled.div`
  font-size: 10px;
  color: #666;
  font-weight: 500;
`;

const AxisLabel = styled.div`
  position: absolute;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  font-family: "Inter", sans-serif;
  
  &.x-axis {
    bottom: -50px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  &.y-axis {
    left: -70px;
    top: 50%;
    transform: translateY(-50%) rotate(-90deg);
    transform-origin: center;
  }
`;



const QuadrantLegend = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  width: 300px;
  margin-left: 24px;
`;

const QuadrantLegendTitle = styled.div`
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 14px;
  color: #2A3039;
`;

const QuadrantLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const QuadrantLegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  
  &.current {
    background-color: #2D70E2;
  }
  
  &.updated {
    background-color: #28a745;
  }
`;

const QuadrantLegendText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const QuadrantLegendLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2A3039;
`;

const QuadrantLegendValue = styled.div`
  font-size: 12px;
  color: #666D79;
`;

// Summary Cards for Quadrant
const DimensionSummary = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

const DimensionCard = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 20px;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

const DimensionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 12px 0;
`;

const DimensionScores = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ScoreGroup = styled.div`
  text-align: center;
`;

const ScoreValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.color || '#495057'};
  margin-bottom: 4px;
`;

const ScoreLabel = styled.div`
  font-size: 11px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ChangeIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: ${props => {
    if (props.value > 0) return '#28a745';
    if (props.value < 0) return '#dc3545';
    return '#6c757d';
  }};
`;

// Question Comparison Styles (refined from existing)
const AnalyticsSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryMetric = styled.div`
  background: white;
  padding: 20px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const QuestionsTable = styled.div`
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 100px 100px 100px;
  background-color: #f8f9fa;
  padding: 16px 12px;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #e9ecef;
  align-items: center;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 100px 100px 100px;
  padding: 16px 12px;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #e9ecef;
    cursor: pointer;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const QuestionText = styled.div`
  font-size: 14px;
  color: #2c3e50;
  line-height: 1.4;
`;

const ScoreCell = styled.div`
  text-align: center;
  font-weight: 500;
  color: ${props => props.color || '#495057'};
  font-size: 14px;
`;

const DifferenceCell = styled.div`
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  color: ${props => {
    if (props.value > 0) return '#28a745';
    if (props.value < 0) return '#dc3545';
    return '#6c757d';
  }};
`;

// Category Comparison Styles
const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
`;

const CategoryCard = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 20px;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

const CategoryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 16px 0;
`;

const CategoryStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StatItem = styled.div`
  text-align: center;
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.color || '#495057'};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 10px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 12px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${props => {
    if (props.change > 0) return '#28a745';
    if (props.change < 0) return '#dc3545';
    return '#6c757d';
  }};
  width: ${props => Math.min(Math.abs(props.change) * 20, 100)}%;
  transition: width 0.3s ease;
`;

// Question Detail Styles
const QuestionDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #E5E8EB;
`;

const AnchorSelector = styled.div`
  display: flex;
  align-items: center;
`;

const AnchorButton = styled.button`
  background: ${props => props.active ? '#2D70E2' : 'none'};
  border: 1px solid ${props => props.active ? '#2D70E2' : '#E5E8EB'};
  color: ${props => props.active ? 'white' : '#666D79'};
  padding: 6px 12px;
  margin-left: 8px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#2D70E2' : '#f8f9fa'};
    border-color: #2D70E2;
    color: ${props => props.active ? 'white' : '#2D70E2'};
  }
`;

const QuestionDetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuestionTitle = styled.h2`
  color: #2A3039;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 16px 0;
`;

const QuestionDetailSummary = styled.div`
  display: flex;
  gap: 24px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const DetailMetric = styled.div`
  text-align: center;
  flex: 1;
`;

const AnchorBreakdownsContainer = styled.div`
  margin-top: 32px;
`;

const AnchorTable = styled.div`
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  overflow: hidden;
`;

const AnchorTableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  background-color: #f8f9fa;
  padding: 12px;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #e9ecef;
  font-size: 12px;
`;

const AnchorTableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  padding: 12px;
  border-bottom: 1px solid #f1f3f5;
  align-items: center;

  &:hover {
    background-color: #fafbfc;
  }
`;

const AnchorCell = styled.div`
  font-size: 13px;
  color: #2A3039;
`;

const AnchorBreakdownScores = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const BreakdownScore = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const BreakdownDetails = styled.div`
  padding-top: 8px;
  border-top: 1px solid #E5E8EB;
`;

// Analytics Helper Functions
const normalizeId = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" || typeof value === "bigint") {
    return Number.isFinite(Number(value)) ? String(value) : null;
  }
  return null;
};

const normalizeLabel = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.toLowerCase() : null;
};

const buildOptionMatcher = (option) => {
  const ids = new Set();
  const labels = new Set();

  const registerId = (candidate) => {
    const normalized = normalizeId(candidate);
    if (normalized) ids.add(normalized);
  };

  const registerLabel = (candidate) => {
    const normalized = normalizeLabel(candidate);
    if (normalized) labels.add(normalized);
  };

  registerId(option?.id);
  registerId(option?.value_id);
  registerId(option?.value);
  registerId(option?.option_id);
  registerId(option?.key);

  registerLabel(option?.name);
  registerLabel(option?.label);
  registerLabel(option?.text);
  registerLabel(option?.value_name);
  registerLabel(option?.value);

  return { ids, labels };
};

const collectPulseCategories = (response) => {
  const results = [];
  const candidates = [
    response?.task_response?.categories,
    response?.task_response?.category,
    response?.task_response?.category_responses,
    response?.task_response?.category_response,
    response?.categories,
    response?.category,
  ];

  candidates.forEach((candidate) => {
    if (!candidate) return;
    if (Array.isArray(candidate)) {
      candidate.forEach((entry) => {
        if (entry) results.push(entry);
      });
      return;
    }

    if (typeof candidate === "object") {
      Object.values(candidate).forEach((value) => {
        if (!value) return;
        if (Array.isArray(value)) {
          value.forEach((entry) => {
            if (entry) results.push(entry);
          });
        } else if (typeof value === "object") {
          results.push(value);
        }
      });
    }
  });

  return results;
};

const collectDebriefCategories = (response) => {
  const results = [];
  const candidates = [response?.response?.categories, response?.categories];

  candidates.forEach((candidate) => {
    if (!candidate) return;
    if (Array.isArray(candidate)) {
      candidate.forEach((entry) => {
        if (entry) results.push(entry);
      });
    }
  });

  return results;
};

const categoryMatchesOption = (categoryEntry, anchorId, optionMatcher) => {
  if (!categoryEntry) return false;

  if (anchorId) {
    const categoryAnchorId = normalizeId(
      categoryEntry?.id ??
        categoryEntry?.category_id ??
        categoryEntry?.anchor_id ??
        categoryEntry?.parent_id ??
        categoryEntry?.category
    );
    if (categoryAnchorId && categoryAnchorId !== anchorId) {
      return false;
    }
  }

  const responseId = normalizeId(
    categoryEntry?.response ??
      categoryEntry?.value ??
      categoryEntry?.value_id ??
      categoryEntry?.option_id ??
      categoryEntry?.id
  );
  if (responseId && optionMatcher.ids.has(responseId)) {
    return true;
  }

  const responseLabel = normalizeLabel(
    categoryEntry?.value_name ??
      categoryEntry?.label ??
      categoryEntry?.name ??
      categoryEntry?.text ??
      categoryEntry?.value
  );
  if (responseLabel && optionMatcher.labels.has(responseLabel)) {
    return true;
  }

  return false;
};

const buildEmployeeCategoryIndex = (records) => {
  const map = new Map();
  (records || []).forEach((record) => {
    const employeeId = normalizeId(
      record?.employee_id ??
        record?.employee ??
        record?.id ??
        record?.user ??
        record?.contact_id ??
        record?.contact ??
        record?.member ??
        record?.person_id
    );
    if (!employeeId) return;

    const categories = Array.isArray(record?.category) ? record.category : [];
    const normalizedCategories = categories
      .map((category) => {
        if (!category) return null;
        return {
          valueId: normalizeId(
            category?.value_id ??
              category?.id ??
              category?.value ??
              category?.category_id ??
              category?.option_id ??
              category?.key
          ),
          label: normalizeLabel(
            category?.value_name ??
              category?.label ??
              category?.name ??
              category?.text ??
              category?.value
          ),
          priority: category?.priority ?? null,
        };
      })
      .filter(Boolean);

    map.set(employeeId, normalizedCategories);
  });
  return map;
};

const resolveFactorLabel = (core_data, dimensionIdZeroBased, factorIdZeroBased) => {
    // Default to 1-based human-readable label
    let fallbackName = `Factor ${factorIdZeroBased + 1}`;
    const dimensions = core_data?.questions?.dimensions;
    if (!dimensions) return fallbackName;
    // Try to find the dimension by exact 0-based id, then 1-based, then index
    const dim = dimensions.find(d => d.id === dimensionIdZeroBased) 
      || dimensions.find(d => d.id === (dimensionIdZeroBased + 1)) 
      || dimensions[dimensionIdZeroBased];
    if (!dim || !Array.isArray(dim.factors)) return fallbackName;
    // Try factor id exact (0-based), then 1-based, then index
    let factorObj = dim.factors.find(f => f.id === factorIdZeroBased)
      || dim.factors.find(f => f.id === (factorIdZeroBased + 1))
      || dim.factors[factorIdZeroBased];
    return factorObj?.title || fallbackName;
  };

  const mapQuestionsAndResponses = (pulseSurveyData, debriefData, core_data) => {
    if (!pulseSurveyData || !debriefData || !core_data) {
      return [];
    }

  const questionComparisons = [];
  const pulseQuestions = pulseSurveyData.questions || [];
  const pulseResponses = pulseSurveyData.responses || [];
  const debriefResponses = debriefData.allResponses || [];

  pulseQuestions.forEach((pulseQuestion, questionIndex) => {
    // Skip Open Text questions in the question comparison table
    const typeValue = (pulseQuestion?.type && (pulseQuestion.type.value || pulseQuestion.type)) || null;
    const typeLabel = pulseQuestion?.type && pulseQuestion.type.label ? String(pulseQuestion.type.label).toLowerCase() : '';
    const questionType = pulseQuestion?.question_type ? String(pulseQuestion.question_type).toLowerCase() : '';
    const isOpenText = (
      (typeof typeValue === 'string' && typeValue.toLowerCase() === 'text') ||
      typeLabel.includes('open text') ||
      questionType === 'openended' ||
      questionType === 'open text' ||
      questionType === 'text'
    );
    if (isOpenText) return;

    if (!pulseQuestion.meta_data) return;

    const { dimension, factor, q_id } = pulseQuestion.meta_data;
    
    // Calculate pulse aggregate score
    const pulseScores = [];
    pulseResponses.forEach(response => {
      const taskList = response.task_response?.task_list || [];
      if (taskList[questionIndex] && typeof taskList[questionIndex].response === 'number') {
        pulseScores.push(taskList[questionIndex].response);
      }
    });

    // Calculate debrief aggregate score
    const debriefScores = [];
    let firstMatchingDebrief = null;
    debriefResponses.forEach(debriefResponse => {
      const questions = debriefResponse.response?.questions || [];
      // Use strict equality (no offsets) to avoid wrong factor selection
      const matchingQuestion = questions.find(f => {
        const dimMatches = f.id === dimension;
        const factorMatches = f.factor === (factor - 1);
        const qMatches = f.q === q_id;
        return dimMatches && factorMatches && qMatches;
      });
      
      if (matchingQuestion && typeof matchingQuestion.response === 'number') {
        debriefScores.push(matchingQuestion.response);
        if (!firstMatchingDebrief) firstMatchingDebrief = matchingQuestion;
      }
    });

    // Check minimum response threshold (handle rule=0 correctly)
    const minimumResponses = core_data?.Whitelabel?.rule !== undefined 
      ? Number(core_data?.Whitelabel?.rule)
      : 2;
    
    // Calculate averages: always compute when any responses exist, but keep threshold in mind for difference calc
    const pulseAvgAny = pulseScores.length > 0 
      ? pulseScores.reduce((sum, score) => sum + score, 0) / pulseScores.length 
      : null;

    const debriefAvgAny = debriefScores.length > 0 
      ? debriefScores.reduce((sum, score) => sum + score, 0) / debriefScores.length 
      : null;

    // For display, prefer the averages even below threshold so the table is populated consistently with the quadrant
    const pulseAverage = pulseAvgAny;
    const debriefAverage = debriefAvgAny;

    // Only include if we have any data on either side
    if (pulseAverage !== null || debriefAverage !== null) {
      // Get dimension and factor names from core_data if available
      let dimensionName = `Dimension ${dimension + 1}`;
      if (core_data?.questions?.dimensions) {
        const coreDimension = core_data.questions.dimensions.find(d => d.id === dimension)
          || core_data.questions.dimensions.find(d => d.id === (dimension + 1))
          || core_data.questions.dimensions[dimension];
        if (coreDimension) {
          dimensionName = coreDimension.title || dimensionName;
        }
      }
      const factorName = resolveFactorLabel(core_data, dimension, factor);

      // Derive debrief-side factor name (if any match was found)
      let debriefFactorName = null;
      if (firstMatchingDebrief) {
        debriefFactorName = resolveFactorLabel(core_data, firstMatchingDebrief.id, firstMatchingDebrief.factor);
      }

      // Console logging for validation
      try {
        console.log('[Factor Mapping] Pulse', {
          dimension,
          factor,
          q_id,
          factorName
        });
        if (firstMatchingDebrief) {
          console.log('[Factor Mapping] Debrief', {
            dimension: firstMatchingDebrief.id,
            factor: firstMatchingDebrief.factor,
            q: firstMatchingDebrief.q,
            factorName: debriefFactorName
          });
        } else {
          console.log('[Factor Mapping] Debrief', 'No matching debrief question found');
        }
      } catch (e) {
        // no-op
      }

      questionComparisons.push({
        questionId: `${dimension}_${factor}_${q_id}`,
        questionText: pulseQuestion.question,
        questionTextFrench: pulseQuestion.questionFrench,
        dimension: dimensionName,
        factor: factorName,
        dimensionId: dimension,
        factorId: factor,
        qId: q_id,
        questionIndex: questionIndex,
        pulseAverage: pulseAverage !== null ? Math.round(pulseAverage * 10) / 10 : null,
        debriefAverage: debriefAverage !== null ? Math.round(debriefAverage * 10) / 10 : null,
        pulseCount: pulseScores.length,
        debriefCount: debriefScores.length,
        difference: (pulseAverage !== null && debriefAverage !== null) 
          ? Math.round((pulseAverage - debriefAverage) * 10) / 10 
          : null
      });
    }
  });

  // Sort by dimension, then factor, then q_id
  questionComparisons.sort((a, b) => {
    if (a.dimensionId !== b.dimensionId) return a.dimensionId - b.dimensionId;
    if (a.factorId !== b.factorId) return a.factorId - b.factorId;
    return a.qId - b.qId;
    });

  return questionComparisons;
};

const calculateDimensionAveragesWithPulseReplacement = (pulseSurveyData, debriefData, core_data) => {
  if (!pulseSurveyData || !debriefData) return [];

  const pulseQuestions = pulseSurveyData.questions || [];
  const pulseResponses = pulseSurveyData.responses || [];
  const debriefResponses = debriefData.allResponses || [];

  // Step 1: Calculate all original dimension averages from survey data
  const originalDimensionScores = {};
  
  debriefResponses.forEach(debriefResponse => {
    const questions = debriefResponse.response?.questions || [];
    questions.forEach(question => {
      if (typeof question.response === 'number') {
        const dimensionId = question.id;
        if (!originalDimensionScores[dimensionId]) {
          originalDimensionScores[dimensionId] = [];
        }
        originalDimensionScores[dimensionId].push(question.response);
      }
    });
  });

  // Step 2: Identify which factors the pulse covers and calculate pulse factor averages
  const pulseFactorAverages = {};
  
  pulseQuestions.forEach((pulseQuestion, questionIndex) => {
    if (!pulseQuestion.meta_data) return;

    const { dimension, factor, q_id } = pulseQuestion.meta_data;
    const factorKey = `${dimension}_${factor}`;
    
    if (!pulseFactorAverages[factorKey]) {
      pulseFactorAverages[factorKey] = {
        dimensionId: dimension,
        factorId: factor,
        scores: []
      };
    }
    
    // Get pulse scores for this question
    pulseResponses.forEach(response => {
      const taskList = response.task_response?.task_list || [];
      if (taskList[questionIndex] && typeof taskList[questionIndex].response === 'number') {
        pulseFactorAverages[factorKey].scores.push(taskList[questionIndex].response);
      }
    });
  });

  // Calculate average scores for each pulse factor
  Object.keys(pulseFactorAverages).forEach(factorKey => {
    const factor = pulseFactorAverages[factorKey];
    if (factor.scores.length > 0) {
      factor.average = factor.scores.reduce((sum, score) => sum + score, 0) / factor.scores.length;
    }
  });

  // Step 3: Calculate updated dimension averages by replacing pulse factors
  const updatedDimensionScores = {};
  
  debriefResponses.forEach(debriefResponse => {
    const questions = debriefResponse.response?.questions || [];
    questions.forEach(question => {
      if (typeof question.response === 'number') {
        const dimensionId = question.id;
        const factorId = question.factor;
        const factorKey = `${dimensionId}_${factorId}`;
        
        if (!updatedDimensionScores[dimensionId]) {
          updatedDimensionScores[dimensionId] = [];
        }
        
        // Use pulse data if available for this factor, otherwise use original survey data
        if (pulseFactorAverages[factorKey] && pulseFactorAverages[factorKey].average !== undefined) {
          updatedDimensionScores[dimensionId].push(pulseFactorAverages[factorKey].average);
        } else {
          updatedDimensionScores[dimensionId].push(question.response);
        }
      }
    });
  });

  // Step 4: Calculate final averages and get dimension names
  const results = [];
  
  // Get proper dimension names from core_data like Dashboard.js does: core_data.questions.dimensions
  const getDimensionName = (dimensionId) => {
    console.log('=== getDimensionName called ===');
    console.log('Input dimensionId:', dimensionId, 'type:', typeof dimensionId);
    console.log('core_data exists:', !!core_data);
    console.log('core_data.questions exists:', !!core_data?.questions);
    console.log('core_data.questions.dimensions exists:', !!core_data?.questions?.dimensions);
    console.log('dimensions array length:', core_data?.questions?.dimensions?.length);
    console.log('Full dimensions array:', core_data?.questions?.dimensions);
    
    // Primary source: core_data.questions.dimensions (like Dashboard.js getFactorLabel function)
    if (core_data?.questions?.dimensions) {
      const dimensionIndex = parseInt(dimensionId);
      console.log('Parsed dimensionIndex:', dimensionIndex);
      
      // Try both direct index and id-based lookup (like Dashboard.js does with d.id - 1 === dimension)
      let dimension = core_data.questions.dimensions[dimensionIndex];
      console.log(`Dimension at index ${dimensionIndex}:`, dimension);
      
      if (!dimension) {
        // Try finding by id like Dashboard.js: d.id - 1 === dimension
        dimension = core_data.questions.dimensions.find(d => (d.id - 1) === dimensionIndex);
        console.log(`Dimension found by id lookup (id-1 === ${dimensionIndex}):`, dimension);
      }
      
      if (!dimension) {
        // Try finding by direct id match
        dimension = core_data.questions.dimensions.find(d => d.id === dimensionIndex);
        console.log(`Dimension found by direct id match (id === ${dimensionIndex}):`, dimension);
      }
      
      if (dimension) {
        console.log('Found dimension object:', dimension);
        if (dimension.title) {
          console.log('Returning dimension.title:', dimension.title);
          return dimension.title;
        }
        if (dimension.name) {
          console.log('Returning dimension.name:', dimension.name);
          return dimension.name;
        }
      }
    }
    
    console.log(`No dimension name found for ID ${dimensionId}, using fallback`);
    
    // Default fallback
    if (dimensionId === 0) return "Performance";
    if (dimensionId === 1) return "Person";
    
    return `Dimension ${parseInt(dimensionId) + 1}`;
  };
  
  Object.keys(originalDimensionScores).forEach(dimensionId => {
    const originalScores = originalDimensionScores[dimensionId];
    const updatedScores = updatedDimensionScores[dimensionId] || originalScores;
    
    const currentAverage = originalScores.length > 0 
      ? originalScores.reduce((sum, score) => sum + score, 0) / originalScores.length 
      : null;
    
    const updatedAverage = updatedScores.length > 0 
      ? updatedScores.reduce((sum, score) => sum + score, 0) / updatedScores.length 
      : null;

    const dimensionName = getDimensionName(dimensionId);
    


    if (currentAverage !== null) {
      results.push({
        dimensionId: parseInt(dimensionId),
        name: dimensionName,
        currentAverage: Math.round(currentAverage * 10) / 10,
        updatedAverage: updatedAverage ? Math.round(updatedAverage * 10) / 10 : Math.round(currentAverage * 10) / 10,
        change: (currentAverage !== null && updatedAverage !== null) 
          ? Math.round((updatedAverage - currentAverage) * 10) / 10 
          : 0
      });
    }
  });

  return results.sort((a, b) => a.dimensionId - b.dimensionId);
};

const calculateCategoryComparisons = (questionComparisons) => {
  const categoryGroups = {};
  
  questionComparisons.forEach(question => {
    if (!categoryGroups[question.factor]) {
      categoryGroups[question.factor] = {
        pulseScores: [],
        debriefScores: []
      };
    }
    
    if (question.pulseAverage !== null) {
      categoryGroups[question.factor].pulseScores.push(question.pulseAverage);
    }
    if (question.debriefAverage !== null) {
      categoryGroups[question.factor].debriefScores.push(question.debriefAverage);
    }
  });

  const results = [];
  Object.keys(categoryGroups).forEach(categoryName => {
    const group = categoryGroups[categoryName];
    
    const currentScore = group.debriefScores.length > 0 
      ? group.debriefScores.reduce((sum, score) => sum + score, 0) / group.debriefScores.length 
      : null;
    
    const pulseScore = group.pulseScores.length > 0 
      ? group.pulseScores.reduce((sum, score) => sum + score, 0) / group.pulseScores.length 
      : null;

    if (currentScore !== null || pulseScore !== null) {
      const change = (currentScore !== null && pulseScore !== null) 
        ? Math.round((pulseScore - currentScore) * 10) / 10 
        : null;

      results.push({
        category: categoryName,
        currentScore: currentScore ? Math.round(currentScore * 10) / 10 : null,
        pulseScore: pulseScore ? Math.round(pulseScore * 10) / 10 : null,
        change: change,
        questionCount: Math.max(group.pulseScores.length, group.debriefScores.length)
      });
    }
  });

  return results.sort((a, b) => a.category.localeCompare(b.category));
};

const ComparisonAnalysis = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [pulseSurveyData, setPulseSurveyData] = useState(null);
  const [debriefData, setDebriefData] = useState(null);
  const [selectedPulseSurvey, setSelectedPulseSurvey] = useState(null);
  const [selectedDebrief, setSelectedDebrief] = useState(null);
  const [activeTab, setActiveTab] = useState('quadrant');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedAnchor, setSelectedAnchor] = useState(null);
  const [initializedFromState, setInitializedFromState] = useState(false);

  // Get core_data from Redux store like Dashboard.js does: state.audit?.core_data
  const core_data = useSelector((state) => state.audit?.core_data, shallowEqual);
  const fetched_scheduled_task = useSelector((state) => state.task_templates?.fetched_scheduled_task, shallowEqual);
  const employees = useSelector((state) => state.employees, shallowEqual);
  const filters = useSelector((state) => state.audit?.new_filters, shallowEqual);
  const employee_category = useSelector((state) => state.employee_category, shallowEqual);
  const employeeCategoryIndex = useMemo(
    () => buildEmployeeCategoryIndex(employee_category?.employee_org_category || []),
    [employee_category]
  );
  
  // If user refreshed this route without navigation state, immediately route to step 1
  useEffect(() => {
    if (!location.state) {
      navigate("/app/otp/otp-comparison/step1", { replace: true });
    }
  }, []);



  // Extract data from navigation state
  useEffect(() => {
    if (location.state) {
      const { 
        pulseSurveyData: pulseData, 
        debriefData: debrief, 
        selectedPulseSurvey: pulse,
        selectedDebrief: debriefSelection
      } = location.state;
      setPulseSurveyData(pulseData || null);
      setDebriefData(debrief || null);
      setSelectedPulseSurvey(pulse || null);
      setSelectedDebrief(debriefSelection || null);
    }
    setInitializedFromState(true);
  }, [location.state]);

  // If the page is refreshed and there is no navigation state to reconstruct selection,
  // redirect back to the Pulse Surveys landing to prevent a stuck loading screen.
  useEffect(() => {
    if (!initializedFromState) return;
    const noNavState = !location.state;
    const noLocalState = !selectedPulseSurvey && !pulseSurveyData && !debriefData;
    if (noNavState && noLocalState) {
      navigate("/app/otp/AI/pulse-survey", { replace: true });
    }
  }, [initializedFromState, location.state, selectedPulseSurvey, pulseSurveyData, debriefData, navigate]);

  // If we have a selected pulse but no built data, fetch and build it here
  useEffect(() => {
    if (selectedPulseSurvey && !pulseSurveyData) {
      dispatch({ type: SG_FETCH_TASK_SCHEDULE, payload: selectedPulseSurvey.id });
    }
  }, [dispatch, selectedPulseSurvey, pulseSurveyData]);

  useEffect(() => {
    if (selectedPulseSurvey && !pulseSurveyData && fetched_scheduled_task && employees?.employees) {
      try {
        const recent = (function getRecentResponses(data, employeesStore, filtersStore, employeeCats){
          const responses = {};
          const employeeList = employeesStore?.employees;
          if (!data?.task_responses || !Array.isArray(data.task_responses)) return [];
          const filteredEmployeeCategories = employeeCats?.filter((employeeCategory) => {
            if (!filtersStore || filtersStore.length === 0) return true;
            return filtersStore.every(filter => {
              const matchingCategory = employeeCategory.category?.find(cat => cat.value_id === filter.id);
              return !!matchingCategory;
            });
          })?.map((employeeCategory) => employeeCategory.employee_id) || [];
          data.task_responses.forEach((response) => {
            const employeeId = Number(response.employee);
            const employeeObj = employeeList?.find((emp) => emp.id === employeeId);
            response.employee_full_name = `${employeeObj?.first_name || ''} ${employeeObj?.last_name || ''}`;
            if (!responses[employeeId] || response.created_at > responses[employeeId].created_at) responses[employeeId] = response;
          });
          return Object.values(responses).filter((response) => {
            if (!filtersStore || filtersStore.length === 0) return true;
            return filteredEmployeeCategories.includes(response.employee);
          });
        })(fetched_scheduled_task, employees, filters || [], employee_category?.employee_org_category || []);

        const participants = Object?.values(fetched_scheduled_task?.user_requests?.user_requests || {}) || [];
        const assignedEmployees = fetched_scheduled_task?.assigned_employees || [];
        const missingParticipants = assignedEmployees.filter(employee => !participants.some(participant => participant.id === employee));
        const missingParticipantsObjects = employees?.employees?.filter(employee => missingParticipants.includes(employee.id)) || [];
        missingParticipantsObjects.forEach(employee => { participants.push({ id: employee.id, email: employee.email || '', email_sent: false, first_name: employee.first_name || '', status: "Pending" }); });
        const questions = fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.filter((task) => task.task_type === "question") || [];
        participants.forEach((participant) => {
          const employeeObj = employees?.employees?.find((emp) => emp.id === participant.id);
          participant.employee_full_name = `${employeeObj?.first_name || ''} ${employeeObj?.last_name || ''}`;
        });
        const complianceData = (function calculateCompliance(participantsList, responsesList){
          if (!participantsList || participantsList.length === 0) return 0;
          const participantIds = participantsList.map(p => p.id);
          const responseEmployeeIds = responsesList.map(r => Number(r.employee));
          const respondedCount = participantIds.filter(id => responseEmployeeIds.includes(id)).length;
          const compliancePercentage = Math.round((respondedCount / participantIds.length) * 100);
          return { percentage: compliancePercentage, respondedCount, totalParticipants: participantIds.length };
        })(participants, recent);
        const surveyStructureData = (function buildSurveyStructureData(core){
          if (!core) return { questionStructure: null, surveyStructure: null, standards: null, extractedQuestions: [], surveyQues: null };
          const questionStructure = core?.questions || null;
          const surveyStructure = core?.categories?.[0] || null;
          const standards = core?.standards || null;
          let extractedQuestions = [];
          let surveyQues = null;
          if (questionStructure?.dimensions) {
            questionStructure.dimensions.forEach((dimension, dimIndex) => {
              if (dimension.subfactors) {
                dimension.subfactors.forEach((subfactor, subfactorIndex) => {
                  if (subfactor.questions) {
                    subfactor.questions.forEach((question, questionIndex) => {
                      extractedQuestions.push({ id: question.id || `${dimIndex}_${subfactorIndex}_${questionIndex}`, text: question.text || question.question || '', dimension: dimension.name || `Dimension ${dimIndex + 1}`, dimensionId: dimension.id || dimIndex, subfactor: subfactor.name || `Subfactor ${subfactorIndex + 1}`, subfactorId: subfactor.id || subfactorIndex, questionIndex: questionIndex, originalQuestion: question });
                    });
                  }
                });
              }
            });
            surveyQues = { questions: questionStructure, sort_order: questionStructure?.sort_order || null };
          }
          return { questionStructure, surveyStructure, standards, extractedQuestions, surveyQues };
        })(core_data);

        const builtPulseData = {
          responses: recent,
          participants,
          questions,
          compliance: complianceData,
          surveyInfo: {
            title: fetched_scheduled_task?.task_template_user_tasks?.name || fetched_scheduled_task?.name || "Unnamed Survey",
            description: fetched_scheduled_task?.description || "",
            createdBy: fetched_scheduled_task?.created_by?.name || "",
            createdDate: fetched_scheduled_task?.created_at || "",
            updatedDate: fetched_scheduled_task?.updated_at || "",
            surveyIsAnonymous: fetched_scheduled_task?.is_anonymous || false,
            surveyIsOpen: fetched_scheduled_task?.status !== 51,
            task_template_id: fetched_scheduled_task?.task_template_user_tasks?.id || null,
          },
          surveyStructure: {
            questionStructure: surveyStructureData.questionStructure,
            surveyStructure: surveyStructureData.surveyStructure,
            standards: surveyStructureData.standards,
            dimensions: surveyStructureData.questionStructure?.dimensions || [],
            categories: surveyStructureData.surveyStructure?.categories || [],
            surveyType: surveyStructureData.questionStructure?.type || null,
            useLikert: surveyStructureData.questionStructure?.useLikert || false,
            surveyQuestions: surveyStructureData.extractedQuestions || [],
            totalQuestions: surveyStructureData.extractedQuestions?.length || 0,
            completeSurveyQuestions: surveyStructureData.surveyQues || null
          },
          rawData: fetched_scheduled_task
        };
        setPulseSurveyData(builtPulseData);
      } catch (e) {
        // no-op
      }
    }
  }, [selectedPulseSurvey, pulseSurveyData, fetched_scheduled_task, employees, filters, employee_category, core_data]);


  // Wait until we process navigation state to avoid false negatives
  if (!initializedFromState) {
    return (
      <PageContainer>
        <Container>
          <PageHeader>
            <HeaderLeft>
              <PageTitle>Data Comparison Analysis</PageTitle>
              <PageSubtitle>Loading selection...</PageSubtitle>
            </HeaderLeft>
          </PageHeader>
        </Container>
      </PageContainer>
    );
  }

  // If debrief or pulse selection missing, show error
  if (!selectedPulseSurvey || !debriefData) {
    return (
      <PageContainer>
        <Container>
          <ErrorMessage>
            Missing selection. Please choose a pulse and a survey to compare.
          </ErrorMessage>
        </Container>
      </PageContainer>
    );
  }

  // If pulse selection is present but pulseSurveyData is still being built, show loading
  if (!pulseSurveyData) {
    return (
      <PageContainer>
        <Container>
          <PageHeader>
            <HeaderLeft>
              <PageTitle>Data Comparison Analysis</PageTitle>
              <PageSubtitle>Preparing pulse data for analysis...</PageSubtitle>
            </HeaderLeft>
          </PageHeader>
        </Container>
      </PageContainer>
    );
  }

  const questionComparisons = mapQuestionsAndResponses(
    pulseSurveyData, 
    debriefData, 
    core_data
  );

  // Only calculate dimension averages if core_data is loaded (like Dashboard.js does)
  const dimensionAverages = (core_data?.questions?.dimensions && pulseSurveyData && debriefData) 
    ? calculateDimensionAveragesWithPulseReplacement(pulseSurveyData, debriefData, core_data)
    : [];
  const categoryComparisons = calculateCategoryComparisons(questionComparisons);

  const renderQuadrantChart = () => {
    // Check if we have core_data loaded (like Dashboard.js does)
    if (!core_data?.questions?.dimensions) {
  return (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666D79' }}>
          <p>Loading survey structure data...</p>
          <p>Core data status: {core_data ? 'Loaded' : 'Not loaded'}</p>
          <p>Questions status: {core_data?.questions ? 'Loaded' : 'Not loaded'}</p>
          <p>Dimensions status: {core_data?.questions?.dimensions ? 'Loaded' : 'Not loaded'}</p>
        </div>
      );
    }
    
    if (dimensionAverages.length < 2) {
      return (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
          <p>Quadrant chart requires at least 2 dimensions with data.</p>
          <p>Found {dimensionAverages.length} dimension(s) with sufficient data.</p>
          <p><strong>Available dimensions:</strong></p>
          {dimensionAverages.map(dim => (
            <p key={dim.dimensionId} style={{ margin: '4px 0' }}>
              • {dim.name}: Current {dim.currentAverage}, Updated {dim.updatedAverage}
            </p>
          ))}
        </div>
      );
    }

    // Use first two dimensions for the quadrant
    const xDimension = dimensionAverages[0];
    const yDimension = dimensionAverages[1];

    // Get pulse factor information for display
    const pulseQuestions = pulseSurveyData.questions || [];
    const pulseFactors = new Set();
    pulseQuestions.forEach(q => {
      if (q.meta_data) {
        pulseFactors.add(`Dimension ${q.meta_data.dimension + 1} - Factor ${q.meta_data.factor + 1}`);
      }
    });

            // Calculate positions like OverviewGrid.js - direct scale positioning  
    const getPosition = (value) => {
      // Use value directly as 10% increments: score 1=10%, 2=20%, etc.
      // This matches the OverviewGrid.js approach of value * 10%
      return value * 10;
    };

    const currentX = getPosition(xDimension.currentAverage || 2.5);
    const currentY = getPosition(yDimension.currentAverage || 2.5);
    const updatedX = getPosition(xDimension.updatedAverage || 2.5);
    const updatedY = getPosition(yDimension.updatedAverage || 2.5);

    console.log('Dimension data:', {
      xDimension: xDimension.name,
      yDimension: yDimension.name,
      currentAvgs: [xDimension.currentAverage, yDimension.currentAverage],
      updatedAvgs: [xDimension.updatedAverage, yDimension.updatedAverage],
      actualPositions: { currentX, currentY, updatedX, updatedY },
      reduxCoreData: core_data,
      coreDimensions: core_data?.questions?.dimensions
    });

    return (
      <QuadrantContainer>
        <DimensionSummary>
          <DimensionCard>
            <DimensionTitle>{xDimension.name}</DimensionTitle>
            <DimensionScores>
              <ScoreGroup>
                <ScoreValue color="#007bff">{xDimension.currentAverage || '—'}</ScoreValue>
                <ScoreLabel>Original Survey</ScoreLabel>
              </ScoreGroup>
              <ChangeIndicator value={xDimension.change || 0}>
                {xDimension.change !== null ? (
                  xDimension.change >= 0 ? `+${xDimension.change}` : xDimension.change
                ) : '—'}
              </ChangeIndicator>
              <ScoreGroup>
                <ScoreValue color="#28a745">{xDimension.updatedAverage || '—'}</ScoreValue>
                <ScoreLabel>With Pulse</ScoreLabel>
              </ScoreGroup>
            </DimensionScores>
          </DimensionCard>
          
          <DimensionCard>
            <DimensionTitle>{yDimension.name}</DimensionTitle>
            <DimensionScores>
              <ScoreGroup>
                <ScoreValue color="#007bff">{yDimension.currentAverage || '—'}</ScoreValue>
                <ScoreLabel>Original Survey</ScoreLabel>
              </ScoreGroup>
              <ChangeIndicator value={yDimension.change || 0}>
                {yDimension.change !== null ? (
                  yDimension.change >= 0 ? `+${yDimension.change}` : yDimension.change
                ) : '—'}
              </ChangeIndicator>
              <ScoreGroup>
                <ScoreValue color="#28a745">{yDimension.updatedAverage || '—'}</ScoreValue>
                <ScoreLabel>With Pulse</ScoreLabel>
              </ScoreGroup>
            </DimensionScores>
          </DimensionCard>
        </DimensionSummary>

        <QuadrantContainer2>
          <div>
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '20px',
              fontSize: '18px',
              fontWeight: '700',
              color: '#333',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Culture of Excellence Summary
            </div>
            <QuadrantChart>
              <QuadrantGrid>
              {/* Quadrant Sections */}
              <QuadrantSection className="top-left">
                <QuadrantTitle>Culture of<br/>CARE</QuadrantTitle>
              </QuadrantSection>
              <QuadrantSection className="top-right">
                <QuadrantTitle>Culture of<br/>QUALITY</QuadrantTitle>
                <ExcellenceBox>
                  <ExcellenceTitle>Culture of</ExcellenceTitle>
                  <ExcellenceText>EXCELLENCE</ExcellenceText>
                </ExcellenceBox>
              </QuadrantSection>
              <QuadrantSection className="bottom-left">
                <QuadrantTitle>Culture of<br/>APATHY</QuadrantTitle>
              </QuadrantSection>
              <QuadrantSection className="bottom-right">
                <QuadrantTitle>Culture of<br/>HARASSMENT</QuadrantTitle>
              </QuadrantSection>
              
              {/* Axis Lines */}
              <QuadrantAxis />
              <QuadrantAxisVertical />
              
              {/* Axis Ticks */}
              <AxisTicks className="horizontal">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i, k) => (
                  <AxisTick key={k}>{i}</AxisTick>
                ))}
              </AxisTicks>
              <AxisTicks className="vertical">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i, k) => (
                  <AxisTick key={k}>{i}</AxisTick>
                ))}
              </AxisTicks>
              
              {/* Axis Labels */}
              <AxisLabel className="x-axis">{xDimension.name}</AxisLabel>
              <AxisLabel className="y-axis">{yDimension.name}</AxisLabel>
              
              {/* Trace Line */}
              {xDimension.currentAverage && yDimension.currentAverage && 
               xDimension.updatedAverage && yDimension.updatedAverage && (
                <TraceLine>
                  <line 
                    x1={`${currentX}%`}
                    y1={`${100 - currentY}%`}
                    x2={`${updatedX}%`}
                    y2={`${100 - updatedY}%`}
                    stroke="#666"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    opacity="0.7"
                  />
                </TraceLine>
              )}
              
                            {/* Data Points - exact positioning */}
              {xDimension.currentAverage && yDimension.currentAverage && (
                <DataPoint 
                  className="current"
                  style={{ 
                    left: `${currentX}%`, 
                    top: `${100 - currentY}%` 
                  }}
                  title={`Original Survey: ${xDimension.name} ${xDimension.currentAverage}, ${yDimension.name} ${yDimension.currentAverage}`}
                />
              )}
              
              {xDimension.updatedAverage && yDimension.updatedAverage && (
                <DataPoint 
                  className="updated"
                  style={{ 
                    left: `${updatedX}%`, 
                    top: `${100 - updatedY}%` 
                  }}
                  title={`With Pulse Data: ${xDimension.name} ${xDimension.updatedAverage}, ${yDimension.name} ${yDimension.updatedAverage}`}
                />
              )}
             </QuadrantGrid>
           </QuadrantChart>
         </div>
                    <QuadrantLegend>
             <QuadrantLegendTitle>Data Points</QuadrantLegendTitle>
             
                           <QuadrantLegendItem>
                <QuadrantLegendDot className="current" />
                <QuadrantLegendText>
                  <QuadrantLegendLabel>Original Survey (Blue)</QuadrantLegendLabel>
                  <QuadrantLegendValue>
                    {xDimension.name}: {xDimension.currentAverage} | {yDimension.name}: {yDimension.currentAverage}
                  </QuadrantLegendValue>
                </QuadrantLegendText>
              </QuadrantLegendItem>
              
              <QuadrantLegendItem>
                <QuadrantLegendDot className="updated" />
                <QuadrantLegendText>
                  <QuadrantLegendLabel>With Pulse Data (Green)</QuadrantLegendLabel>
                  <QuadrantLegendValue>
                    {xDimension.name}: {xDimension.updatedAverage} | {yDimension.name}: {yDimension.updatedAverage}
                  </QuadrantLegendValue>
                </QuadrantLegendText>
              </QuadrantLegendItem>
             
             {pulseFactors.size > 0 && (
               <div style={{ 
                 marginTop: '20px', 
                 padding: '16px 0',
                 borderTop: '1px solid #E5E8EB'
               }}>
                 <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#2A3039', fontWeight: '600' }}>
                   Pulse Data Replaces:
                 </h4>
                 <div style={{ fontSize: '12px', color: '#666D79' }}>
                   {Array.from(pulseFactors).map((factor, index) => (
                     <div key={index} style={{ margin: '2px 0' }}>• {factor}</div>
                   ))}
                 </div>
               </div>
             )}
           </QuadrantLegend>
         </QuadrantContainer2>
      </QuadrantContainer>
    );
  };

  const renderQuestionComparison = () => {
    if (!core_data?.questions?.dimensions) {
      return (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666D79' }}>
          <p>Loading survey structure data...</p>
        </div>
      );
    }

    if (selectedQuestion) {
      return renderQuestionDetail();
    }

    return renderQuestionSelection();
  };

  const renderQuestionSelection = () => {
            if (!questionComparisons.length) {
              return (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666D79' }}>
          <p>No matching questions found between pulse survey and survey data.</p>
          <p><strong>Debug Information:</strong></p>
          <p>• Pulse Questions: {pulseSurveyData?.questions?.length || 0}</p>
          <p>• Pulse Responses: {pulseSurveyData?.responses?.length || 0}</p>
          <p>• Survey Responses: {debriefData?.allResponses?.length || 0}</p>
        </div>
              );
            }

            // Calculate summary metrics
            const questionsWithBothScores = questionComparisons.filter(q => q.pulseAverage !== null && q.debriefAverage !== null).length;
            const avgPulseScore = questionComparisons
              .filter(q => q.pulseAverage !== null)
              .reduce((sum, q, _, arr) => sum + q.pulseAverage / arr.length, 0);
            const avgDebriefScore = questionComparisons
              .filter(q => q.debriefAverage !== null)
              .reduce((sum, q, _, arr) => sum + q.debriefAverage / arr.length, 0);

            return (
      <div>
                <AnalyticsSummary>
                  <SummaryMetric>
                    <MetricValue>{avgPulseScore.toFixed(1)}</MetricValue>
                    <MetricLabel>Average Pulse Score</MetricLabel>
                  </SummaryMetric>
                  <SummaryMetric>
                    <MetricValue>{avgDebriefScore.toFixed(1)}</MetricValue>
                    <MetricLabel>Average Survey Score</MetricLabel>
                  </SummaryMetric>
                  <SummaryMetric>
                    <MetricValue style={{ color: (avgPulseScore - avgDebriefScore) >= 0 ? '#28a745' : '#dc3545' }}>
                      {(avgPulseScore - avgDebriefScore) >= 0 ? '+' : ''}{(avgPulseScore - avgDebriefScore).toFixed(1)}
                    </MetricValue>
                    <MetricLabel>Average Change</MetricLabel>
                  </SummaryMetric>
                </AnalyticsSummary>

                <QuestionsTable>
                  <TableHeader>
                    <div>Question</div>
                    <div>Pulse Score</div>
                    <div>Survey Score</div>
                    <div>Difference</div>
                  </TableHeader>
                  
          {questionComparisons.map((question) => (
            <TableRow 
              key={question.questionId} 
              onClick={() => setSelectedQuestion(question)}
              style={{ cursor: 'pointer' }}
            >
                        <QuestionText>{question.questionText}</QuestionText>
                      
              <ScoreCell color={question.pulseAverage ? '#2A3039' : '#666D79'}>
                        {question.pulseAverage !== null ? question.pulseAverage : '—'}
                      </ScoreCell>
                      
              <ScoreCell color={question.debriefAverage ? '#2A3039' : '#666D79'}>
                        {question.debriefAverage !== null ? question.debriefAverage : '—'}
                      </ScoreCell>
                      
                      <DifferenceCell value={question.difference}>
                        {question.difference !== null 
                          ? `${question.difference > 0 ? '+' : ''}${question.difference}`
                          : '—'
                        }
                      </DifferenceCell>
                    </TableRow>
                  ))}
                </QuestionsTable>

        <div style={{ marginTop: '16px', fontSize: '12px', color: '#666D79', textAlign: 'center' }}>
                  <strong>Note:</strong> Scores are averaged across all responses. 
                  Positive differences indicate pulse scores are higher than survey scores.
          <br />
          <strong>Click on any question</strong> to view detailed comparison with anchor breakdowns.
                </div>
      </div>
    );
  };

  const renderQuestionDetail = () => {
    // Build anchor categories, filtering out primary and preferring secondary as default
    const anchorCategoriesRaw = core_data?.categories?.[0]?.categories || [];
    const filteredAnchorCategories = anchorCategoriesRaw.filter(cat => cat?.priority !== 'primary');
    const preferredSecondary = filteredAnchorCategories.find(cat => cat?.priority === 'secondary');
    const defaultAnchorId = (preferredSecondary?.id) || (filteredAnchorCategories[0]?.id) || (anchorCategoriesRaw[0]?.id);
    const currentAnchor = selectedAnchor || defaultAnchorId;
    
    return (
      <div>
        <QuestionDetailHeader>
          <button 
            onClick={() => setSelectedQuestion(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#2D70E2',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            ← Back to Questions
          </button>
          
          {filteredAnchorCategories.length > 1 && (
            <AnchorSelector>
              <span style={{ color: '#666D79', fontSize: '14px', marginRight: '8px' }}>
                View by:
              </span>
              {filteredAnchorCategories.map(category => (
                <AnchorButton
                  key={category.id}
                  active={currentAnchor === category.id}
                  onClick={() => setSelectedAnchor(category.id)}
                >
                  {category.name}
                </AnchorButton>
              ))}
            </AnchorSelector>
          )}
        </QuestionDetailHeader>

        <QuestionDetailContent>
          <QuestionTitle>{selectedQuestion.questionText}</QuestionTitle>
          
          <QuestionDetailSummary>
            <DetailMetric>
              <MetricValue>{selectedQuestion.pulseAverage || '—'}</MetricValue>
              <MetricLabel>Pulse Score</MetricLabel>
            </DetailMetric>
            <DetailMetric>
              <MetricValue>{selectedQuestion.debriefAverage || '—'}</MetricValue>
              <MetricLabel>Survey Score</MetricLabel>
            </DetailMetric>
            <DetailMetric>
              <MetricValue style={{ color: selectedQuestion.difference >= 0 ? '#28a745' : '#dc3545' }}>
                {selectedQuestion.difference !== null 
                  ? `${selectedQuestion.difference > 0 ? '+' : ''}${selectedQuestion.difference.toFixed(1)}`
                  : '—'
                }
              </MetricValue>
              <MetricLabel>Change</MetricLabel>
            </DetailMetric>
          </QuestionDetailSummary>

          {currentAnchor && renderAnchorBreakdowns(selectedQuestion, currentAnchor)}
        </QuestionDetailContent>
      </div>
    );
  };

  const renderAnchorBreakdowns = (question, anchorId) => {
    const anchorCategory = core_data?.categories?.[0]?.categories?.find(cat => cat.id === anchorId);
    if (!anchorCategory) return null;

    const breakdowns = calculateQuestionAnchorBreakdowns(question, anchorId);
    
    return (
      <AnchorBreakdownsContainer>
        <h3 style={{ color: '#2A3039', fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
          Comparison by {anchorCategory.name}
        </h3>
        
        {breakdowns.length === 0 ? (
          <div style={{ color: '#666D79', fontStyle: 'italic' }}>
            Insufficient data for breakdown analysis
          </div>
        ) : (
          <AnchorTable>
            <AnchorTableHeader>
              <div>Category</div>
              <div>Pulse</div>
              <div>Survey</div>
              <div>Change</div>
              <div>Counts</div>
            </AnchorTableHeader>
            {breakdowns.map((breakdown, index) => (
              <AnchorTableRow key={index}>
                <AnchorCell>{breakdown.categoryName}</AnchorCell>
                <AnchorCell style={{ color: '#2D70E2' }}>{
                  breakdown.pulseScore !== null ? breakdown.pulseScore.toFixed(1) : '—'
                }</AnchorCell>
                <AnchorCell style={{ color: '#666D79' }}>{
                  breakdown.debriefScore !== null ? breakdown.debriefScore.toFixed(1) : '—'
                }</AnchorCell>
                <AnchorCell style={{ color: breakdown.change >= 0 ? '#28a745' : '#dc3545' }}>{
                  breakdown.change !== null ? `${breakdown.change > 0 ? '+' : ''}${breakdown.change.toFixed(1)}` : '—'
                }</AnchorCell>
                <AnchorCell style={{ color: '#666D79' }}>
                  {breakdown.pulseCount} / {breakdown.debriefCount}
                </AnchorCell>
              </AnchorTableRow>
            ))}
          </AnchorTable>
        )}
      </AnchorBreakdownsContainer>
    );
  };

  const calculateQuestionAnchorBreakdowns = (question, anchorId) => {
    if (!pulseSurveyData || !debriefData || !core_data) return [];

    const normalizedAnchorId = normalizeId(anchorId);
    const anchorCategories = core_data?.categories?.[0]?.categories || [];
    const anchorCategory =
      anchorCategories.find((cat) => normalizeId(cat?.id) === normalizedAnchorId) ||
      anchorCategories.find((cat) => cat?.id === anchorId);

    if (!anchorCategory) return [];

    const anchorPriority = anchorCategory?.priority ?? null;
    const breakdowns = [];

    (anchorCategory?.options || []).forEach((option) => {
      if (!option) return;
      const optionMatcher = buildOptionMatcher(option);
      const pulseScores = [];
      const debriefScores = [];

      (pulseSurveyData.responses || []).forEach((response) => {
        const pulseCategories = collectPulseCategories(response);
        let matches = pulseCategories.some((categoryEntry) =>
          categoryMatchesOption(categoryEntry, normalizedAnchorId, optionMatcher)
        );

        if (!matches) {
          const employeeKey = normalizeId(
            response?.employee ??
              response?.task_response?.employee ??
              response?.task_response?.employee_id
          );
          if (employeeKey && employeeCategoryIndex.has(employeeKey)) {
            const employeeCategories = employeeCategoryIndex.get(employeeKey) || [];
            matches = employeeCategories.some((categoryEntry) => {
              if (
                anchorPriority &&
                categoryEntry?.priority &&
                categoryEntry.priority !== anchorPriority
              ) {
                return false;
              }
              if (categoryEntry?.valueId && optionMatcher.ids.has(categoryEntry.valueId)) {
                return true;
              }
              if (categoryEntry?.label && optionMatcher.labels.has(categoryEntry.label)) {
                return true;
              }
              return false;
            });
          }
        }

        if (!matches) return;

        const taskList = response?.task_response?.task_list;
        if (!Array.isArray(taskList)) return;

        let taskResponse = null;

        if (
          Number.isInteger(question?.questionIndex) &&
          question.questionIndex >= 0 &&
          question.questionIndex < taskList.length
        ) {
          taskResponse = taskList[question.questionIndex];
        }

        if (!taskResponse) {
          taskResponse = taskList.find((task) => {
            if (!task?.meta_data) return false;
            const taskDimensionId = normalizeId(task.meta_data.dimension);
            const taskFactorId = normalizeId(task.meta_data.factor);
            const taskQuestionId = normalizeId(task.meta_data.q_id);

            const questionDimensionId = normalizeId(question.dimensionId);
            const questionFactorId = normalizeId(question.factorId);
            const questionQuestionId = normalizeId(question.qId);

            let factorMatches = taskFactorId === questionFactorId;
            if (!factorMatches) {
              const taskFactorNumeric = Number(task.meta_data.factor);
              const questionFactorNumeric = Number(question.factorId);
              if (
                Number.isFinite(taskFactorNumeric) &&
                Number.isFinite(questionFactorNumeric)
              ) {
                factorMatches =
                  taskFactorNumeric === questionFactorNumeric ||
                  taskFactorNumeric === questionFactorNumeric - 1;
              }
            }

            return (
              taskDimensionId === questionDimensionId &&
              factorMatches &&
              taskQuestionId === questionQuestionId
            );
          });
        }

        if (!taskResponse) return;

        const numericResponse = Number(taskResponse?.response);
        if (Number.isFinite(numericResponse)) {
          pulseScores.push(numericResponse);
        }
      });

      (debriefData.allResponses || []).forEach((response) => {
        const debriefCategories = collectDebriefCategories(response);
        const matches = debriefCategories.some((categoryEntry) =>
          categoryMatchesOption(categoryEntry, normalizedAnchorId, optionMatcher)
        );
        if (!matches) return;

        const questions = response?.response?.questions || [];
        const dimensionTarget = Number(question.dimensionId);
        const factorTarget =
          question?.factorId != null && Number.isFinite(Number(question.factorId))
            ? Number(question.factorId) - 1
            : null;
        const qTarget = Number(question.qId);

        const matchingQuestion = questions.find((q) => {
          const dimMatches = Number(q?.id) === dimensionTarget;
          const qMatches = Number(q?.q) === qTarget;
          let factorMatches = true;

          if (factorTarget !== null && Number.isFinite(factorTarget)) {
            factorMatches = Number(q?.factor) === factorTarget;
          }

          return dimMatches && factorMatches && qMatches;
        });

        if (!matchingQuestion) return;

        const numericResponse = Number(matchingQuestion?.response);
        if (Number.isFinite(numericResponse)) {
          debriefScores.push(numericResponse);
        }
      });

      const pulseAvg =
        pulseScores.length > 0
          ? pulseScores.reduce((sum, score) => sum + score, 0) / pulseScores.length
          : null;
      const debriefAvg =
        debriefScores.length > 0
          ? debriefScores.reduce((sum, score) => sum + score, 0) /
            debriefScores.length
          : null;

      if (pulseAvg !== null || debriefAvg !== null) {
        const change =
          pulseAvg !== null && debriefAvg !== null ? pulseAvg - debriefAvg : null;

        breakdowns.push({
          categoryName:
            option?.name ??
            option?.label ??
            option?.text ??
            option?.value_name ??
            option?.value ??
            "Category",
          pulseScore: pulseAvg,
          debriefScore: debriefAvg,
          change,
          pulseCount: pulseScores.length,
          debriefCount: debriefScores.length,
        });
      }
    });

    return breakdowns.sort((a, b) => (b.change || 0) - (a.change || 0));
  };

  const renderCategoryComparison = () => {
    if (!categoryComparisons.length) {
      return (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666D79' }}>
          <p>No factor data available for comparison.</p>
        </div>
      );
    }

    return (
      <CategoryGrid>
        {categoryComparisons.map((category) => (
          <CategoryCard key={category.category}>
            <CategoryTitle>{category.category}</CategoryTitle>
            
            <CategoryStats>
              <StatItem>
                <StatValue color="#007bff">{category.currentScore || '—'}</StatValue>
                <StatLabel>Current Score</StatLabel>
              </StatItem>
              
              <StatItem>
                <ChangeIndicator value={category.change || 0}>
                  {category.change !== null ? (
                    category.change >= 0 ? `+${category.change}` : category.change
                  ) : '—'}
                </ChangeIndicator>
                <StatLabel>Change</StatLabel>
              </StatItem>
              
              <StatItem>
                <StatValue color="#28a745">{category.pulseScore || '—'}</StatValue>
                <StatLabel>Pulse Score</StatLabel>
              </StatItem>
            </CategoryStats>
            
            <ProgressBar>
              <ProgressFill change={category.change || 0} />
            </ProgressBar>
            
            <div style={{ fontSize: '11px', color: '#666D79', marginTop: '8px', textAlign: 'center' }}>
              {category.questionCount} question{category.questionCount !== 1 ? 's' : ''}
            </div>
          </CategoryCard>
        ))}
      </CategoryGrid>
    );
  };

  if (!pulseSurveyData || !debriefData || !core_data) {
    return (
      <PageContainer>
        <Container>
          <PageHeader>
            <HeaderLeft>
              <PageTitle>Data Comparison Analysis</PageTitle>
              <PageSubtitle>Loading comparison data...</PageSubtitle>
            </HeaderLeft>
          </PageHeader>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <Breadcrumbs>
          <Crumb clickable onClick={() => navigate("/app/otp/AI/pulse-survey")}>Pulse Surveys</Crumb>
          <span>/</span>
          <Crumb>Comparison</Crumb>
          <span>/</span>
          <Crumb>Analysis</Crumb>
        </Breadcrumbs>
        <PageHeader>
          <HeaderLeft>
            <PageTitle>Data Comparison Analysis</PageTitle>
            <PageSubtitle>Comprehensive analytics comparing pulse survey responses with survey findings</PageSubtitle>
          </HeaderLeft>
        </PageHeader>

        <TabContainer>
          <TabNavigation>
            <TabButton 
              active={activeTab === 'quadrant'} 
              onClick={() => {
                setActiveTab('quadrant');
                setSelectedQuestion(null);
                setSelectedAnchor(null);
              }}
            >
              Quadrant Chart
            </TabButton>
            <TabButton 
              active={activeTab === 'questions'} 
              onClick={() => {
                setActiveTab('questions');
                setSelectedQuestion(null);
                setSelectedAnchor(null);
              }}
            >
              Question Comparison
            </TabButton>
            <TabButton 
              active={activeTab === 'categories'} 
              onClick={() => {
                setActiveTab('categories');
                setSelectedQuestion(null);
                setSelectedAnchor(null);
              }}
            >
              Factor Analysis
            </TabButton>
            <TabButton
              active={activeTab === "participation"}
              onClick={() => {
                setActiveTab("participation");
                setSelectedQuestion(null);
                setSelectedAnchor(null);
              }}
            >
              Participation
            </TabButton>
          </TabNavigation>

          <TabContent>
            {activeTab === 'quadrant' && renderQuadrantChart()}
            {activeTab === 'questions' && renderQuestionComparison()}
            {activeTab === 'categories' && renderCategoryComparison()}
            {activeTab === "participation" && (
              <ComparisonParticipationTab
                pulseSurveyData={pulseSurveyData}
                debriefData={debriefData}
                selectedDebrief={selectedDebrief}
                coreData={core_data}
                employeeCategories={employee_category?.employee_org_category || []}
              />
            )}
          </TabContent>
        </TabContainer>
      </Container>
    </PageContainer>
  );
};

export default ComparisonAnalysis; 